import { useState, type ReactNode } from 'react'
import { Card, CardBody, PageHeader } from '@/components/Card'
import { useAuth } from '@/hooks/useAuth'

/*
 * Guides are static content, deliberately.
 *
 * They describe this specific server — which apps are worth installing, which
 * settings avoid transcoding on this hardware — so they belong in the app
 * rather than in a CMS nobody would maintain. Keeping them in the bundle also
 * means they still work during an outage, which is exactly when someone is
 * likely to be reading "why won't this play".
 */

interface Guide {
  id: string
  title: string
  summary: string
  body: ReactNode
}

/** A recommended app, with the reason it is recommended. */
function App({ name, note, url }: { name: string; note: string; url?: string }) {
  return (
    <li className="board-row py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="signage-tight text-sm text-ink underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
          >
            {name}
          </a>
        ) : (
          <span className="signage-tight text-sm text-ink">{name}</span>
        )}
      </div>
      <p className="mt-1 text-sm text-muted">{note}</p>
    </li>
  )
}

function Step({ children }: { children: ReactNode }) {
  return <li className="text-sm text-muted">{children}</li>
}

function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="mt-3 list-decimal space-y-2 pl-5 marker:font-mono marker:text-[11px] marker:text-accent">
      {children}
    </ol>
  )
}

function buildGuides(serverUrl: string, serverName: string): Guide[] {
  return [
    {
      id: 'iphone',
      title: 'iPhone and iPad',
      summary: 'Streamyfin is the one to install.',
      body: (
        <>
          <ul className="mt-1">
            <App
              name="Streamyfin"
              url="https://apps.apple.com/app/streamyfin/id6593660679"
              note="The best option here — this server runs the Streamyfin plugin, so requesting is built into the app and you can browse and ask for something without leaving it."
            />
            <App
              name="Infuse"
              url="https://firecore.com/infuse"
              note="Excellent playback and handles almost any file without transcoding. Paid, and it has no request feature."
            />
            <App
              name="Jellyfin (official)"
              url="https://apps.apple.com/app/jellyfin-mobile/id1480192618"
              note="Free and dependable, but plays fewer formats directly, so it leans on the server to transcode more often."
            />
          </ul>
          <Steps>
            <Step>
              Install the app, then add a server: <code className="font-mono text-ink">{serverUrl}</code>
            </Step>
            <Step>Sign in with the same username and password you use here.</Step>
            <Step>
              In Streamyfin, set the request server to this portal's request page so asking for
              something happens in-app.
            </Step>
          </Steps>
        </>
      ),
    },
    {
      id: 'tv',
      title: 'Apple TV, Android TV and Fire Stick',
      summary: 'One app per platform, and which to avoid.',
      body: (
        <>
          <ul className="mt-1">
            <App
              name="Apple TV — Infuse or Swiftfin"
              note="Infuse is the smoothest and plays the widest range of files. Swiftfin is free and open source, and is the closest to the Jellyfin experience."
            />
            <App
              name="Android TV / Google TV — Jellyfin"
              note="The official app is the right choice and handles 4K and HDR well on most boxes."
            />
            <App
              name="Fire Stick — Jellyfin"
              note="Works, but older Fire Sticks struggle with high-bitrate files. If playback stutters, that is the stick, not the server."
            />
            <App
              name="Roku — Jellyfin"
              note="Usable, but Roku direct-plays the least, so it makes the server transcode most often. Prefer another device if you have one."
            />
          </ul>
          <Steps>
            <Step>
              Add the server as <code className="font-mono text-ink">{serverUrl}</code> — no port,
              no VPN needed.
            </Step>
            <Step>Sign in with your usual account.</Step>
          </Steps>
        </>
      ),
    },
    {
      id: 'playback',
      title: 'Getting the best picture',
      summary: 'Direct play, and why quality sometimes drops.',
      body: (
        <>
          <p className="text-sm text-muted">
            When your device can play a file as-is, that is <span className="text-ink">direct
            play</span> — full quality, no strain on the server. When it cannot, the server has to
            rebuild the video on the fly, which lowers quality and can stutter if several people do
            it at once.
          </p>
          <Steps>
            <Step>
              Set quality to <span className="text-ink">Auto</span> or the highest option rather
              than a fixed low bitrate — a fixed setting forces a rebuild even when your device
              could have played the file directly.
            </Step>
            <Step>
              On a TV, prefer Infuse, Swiftfin or the official app over a browser. Browsers play the
              fewest formats.
            </Step>
            <Step>
              If something stutters, try a different app on the same device before assuming the
              server is at fault — it usually is not.
            </Step>
          </Steps>
        </>
      ),
    },
    {
      id: 'requesting',
      title: 'Asking for something',
      summary: 'How requests work, and how long they take.',
      body: (
        <>
          <p className="text-sm text-muted">
            Use <span className="text-ink">Request</span> on the board. Once approved it is found,
            downloaded and added to {serverName} automatically — your board shows exactly where it
            has got to, and you get a notification when it is ready.
          </p>
          <Steps>
            <Step>
              Films that are still in cinemas cannot be downloaded yet. Those show as{' '}
              <span className="text-ink">Not out yet</span> with their release date, and will start
              on their own afterwards.
            </Step>
            <Step>
              For a series, ask for the seasons you want — each season is tracked separately.
            </Step>
            <Step>
              A long-running download usually means few people are sharing it, not that it is stuck.
              It will finish.
            </Step>
          </Steps>
        </>
      ),
    },
    {
      id: 'offline',
      title: 'Watching without internet',
      summary: 'Download to your phone before a flight.',
      body: (
        <p className="text-sm text-muted">
          Streamyfin, Infuse and the official app can all download an episode or film to your device
          for offline watching. Do it on wi-fi before you travel — downloading and watching are the
          same load on the server, so there is no penalty for grabbing a few things in advance.
        </p>
      ),
    },
    {
      id: 'live',
      title: 'Live sports',
      summary: 'Adding the live channel list.',
      body: (
        <p className="text-sm text-muted">
          Live sports run through a separate channel list with its own sign-in. Open{' '}
          <a
            href="https://live.replogle.dev"
            target="_blank"
            rel="noreferrer"
            className="text-ink underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
          >
            live.replogle.dev
          </a>
          , sign in with your {serverName} account, and use the Jellyfin button there to get a
          personal playlist link you can add to any IPTV app. The board shows whether it is up.
        </p>
      ),
    },
  ]
}

/**
 * Accordion rather than sub-pages: these are short, and someone setting up a
 * new device wants to scan the list and open one, not navigate a hierarchy.
 */
function GuideItem({ guide, open, onToggle }: { guide: Guide; open: boolean; onToggle: () => void }) {
  return (
    <div className="board-row">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-elevated"
      >
        <span className="min-w-0 flex-1">
          <span className="signage-tight block text-sm text-ink">{guide.title}</span>
          <span className="mt-0.5 block text-xs text-faint">{guide.summary}</span>
        </span>
        <svg
          viewBox="0 0 24 24"
          className={`size-4 shrink-0 text-faint transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      {open ? <div className="border-t border-line px-4 pt-3 pb-4">{guide.body}</div> : null}
    </div>
  )
}

export function GuidesPage() {
  const { server } = useAuth()
  const serverUrl = server?.jellyfinUrl ?? 'https://watch.replogle.dev'
  const serverName = server?.name ?? 'the server'
  const guides = buildGuides(serverUrl, serverName)

  const [openId, setOpenId] = useState<string | null>(guides[0]?.id ?? null)

  return (
    <div>
      <PageHeader
        title="Guides"
        description="Which app to install, and how to get the best out of the server."
      />

      <Card>
        <CardBody flush>
          {guides.map((guide) => (
            <GuideItem
              key={guide.id}
              guide={guide}
              open={openId === guide.id}
              onToggle={() => setOpenId(openId === guide.id ? null : guide.id)}
            />
          ))}
        </CardBody>
      </Card>

      <p className="mt-4 text-xs text-faint">
        Something missing or wrong here? Tell whoever runs the server.
      </p>
    </div>
  )
}
